<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import { categoryIcons } from "../../lib/icons";
  import type { Trip, Schedule } from "../../lib/types";
  import { schedulesApi } from "../../lib/api";
  import { addNotification } from "../../lib/stores";
  import Layout from "../../components/layout/Layout.svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Modal from "../../components/overlay/Modal.svelte";
  import EmptyState from "../../components/feedback/EmptyState.svelte";
  import DaySelector from "../../components/input/DaySelector.svelte";
  import Button from "../../components/input/Button.svelte";

  let { trip, schedules = [], user = null }: {
    trip: Trip;
    schedules?: Schedule[];
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  let showCreateModal = $state(false);
  let editingSchedule: Schedule | null = $state(null);
  let loading = $state(false);
  let selectedDate = $state("");
  let selectedDateHydrated = false;

  // 여행 기간 내 날짜 목록 생성
  let tripDates = $derived(generateDateRange(trip.startDate, trip.endDate));

  // 선택된 날짜의 일정들
  let filteredSchedules = $derived(schedules
    .filter(s => s.date === selectedDate)
    .sort((a, b) => (a.startTime || "00:00").localeCompare(b.startTime || "00:00")));

  // 날짜별 일정 개수
  let scheduleCounts = $derived(schedules.reduce((acc, s) => {
    acc[s.date] = (acc[s.date] || 0) + 1;
    return acc;
  }, {} as Record<string, number>));

  let newSchedule = $state({
    title: "",
    location: "",
    date: "",
    startTime: "09:00",
    endTime: "10:00",
    memo: "",
    category: "activity" as string,
  });

  $effect(() => {
    if (selectedDateHydrated) return;
    selectedDate = trip.startDate;
    newSchedule.date = trip.startDate;
    selectedDateHydrated = true;
  });

  $effect(() => {
    if (!editingSchedule) {
      newSchedule.date = selectedDate;
    }
  });

  const categories = [
    { key: "activity",      label: "액티비티", icon: categoryIcons.activity,      color: "bg-blue-100 text-blue-700" },
    { key: "food",          label: "식사",     icon: categoryIcons.food,          color: "bg-orange-100 text-orange-700" },
    { key: "transport",     label: "이동",     icon: categoryIcons.transport,     color: "bg-green-100 text-green-700" },
    { key: "accommodation", label: "숙소",     icon: categoryIcons.accommodation, color: "bg-purple-100 text-purple-700" },
    { key: "shopping",      label: "쇼핑",     icon: categoryIcons.shopping,      color: "bg-pink-100 text-pink-700" },
    { key: "other",         label: "기타",     icon: categoryIcons.other,         color: "bg-surface-hover text-secondary" },
  ];

  function generateDateRange(start: string, end: string): string[] {
    const dates: string[] = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split("T")[0]);
    }
    return dates;
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
    return `${date.getMonth() + 1}/${date.getDate()} (${weekdays[date.getDay()]})`;
  }

  function getDayNumber(dateStr: string): number {
    return tripDates.indexOf(dateStr) + 1;
  }

  function getCategoryInfo(key: string) {
    return categories.find(c => c.key === key) || categories[categories.length - 1];
  }

  async function createSchedule() {
    loading = true;
    try {
      const response = await schedulesApi.create(trip.id, {
        title: newSchedule.title,
        location: newSchedule.location,
        date: newSchedule.date,
        startTime: newSchedule.startTime,
        endTime: newSchedule.endTime,
        memo: newSchedule.memo,
        category: newSchedule.category,
      });

      if (response.success) {
        addNotification("success", "일정이 추가되었습니다!");
        showCreateModal = false;
        resetForm();
        router.reload();
      }
    } catch (error: any) {
      addNotification("error", error.response?.data?.errors?.[0]?.message || "일정 추가에 실패했습니다.");
    } finally {
      loading = false;
    }
  }

  async function updateSchedule() {
    if (!editingSchedule) return;
    
    loading = true;
    try {
      const response = await schedulesApi.update(editingSchedule.id, {
        title: newSchedule.title,
        location: newSchedule.location,
        date: newSchedule.date,
        startTime: newSchedule.startTime,
        endTime: newSchedule.endTime,
        memo: newSchedule.memo,
        category: newSchedule.category,
      });

      if (response.success) {
        addNotification("success", "일정이 수정되었습니다!");
        editingSchedule = null;
        resetForm();
        router.reload();
      }
    } catch (error: any) {
      addNotification("error", error.response?.data?.errors?.[0]?.message || "일정 수정에 실패했습니다.");
    } finally {
      loading = false;
    }
  }

  async function deleteSchedule(id: string) {
    if (!confirm("이 일정을 삭제하시겠습니까?")) return;
    
    try {
      await schedulesApi.delete(id);
      addNotification("success", "일정이 삭제되었습니다.");
      router.reload();
    } catch (error: any) {
      addNotification("error", "일정 삭제에 실패했습니다.");
    }
  }

  function openEditModal(schedule: Schedule) {
    editingSchedule = schedule;
    newSchedule = {
      title: schedule.title,
      location: schedule.location || "",
      date: schedule.date,
      startTime: schedule.startTime || "09:00",
      endTime: schedule.endTime || "10:00",
      memo: schedule.memo || "",
      category: schedule.category || "activity",
    };
  }

  function resetForm() {
    newSchedule = {
      title: "",
      location: "",
      date: selectedDate,
      startTime: "09:00",
      endTime: "10:00",
      memo: "",
      category: "activity",
    };
  }

  function closeModal() {
    showCreateModal = false;
    editingSchedule = null;
    resetForm();
  }
</script>

<Layout {user}>
  <div class="min-h-screen bg-app">
    <!-- Trip Sub Navigation -->
    <TripSubNav {trip} currentPage="schedule" />

    <!-- Date Selector -->
    <header class="border-b border-white/10 bg-slate-950/60">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <DaySelector bind:selected={selectedDate} dates={tripDates} />
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Add Schedule Button -->
      <div class="mb-6">
        <Button variant="secondary" fullWidth onclick={() => { newSchedule.date = selectedDate; showCreateModal = true; }}>+ 일정 추가하기</Button>
      </div>

      <!-- Timeline -->
      {#if filteredSchedules.length === 0}
        <EmptyState
          icon="schedule"
          title="아직 일정이 없습니다"
          description="이 날의 일정을 추가해보세요!"
        >
          <Button variant="primary" onclick={() => { newSchedule.date = selectedDate; showCreateModal = true; }}>일정 추가하기</Button>
        </EmptyState>
      {:else}
        <div class="space-y-4">
          {#each filteredSchedules as schedule, i}
            {@const categoryInfo = getCategoryInfo(schedule.category || "other")}
            <div class="relative flex gap-4">
              <!-- Timeline Line -->
              {#if i < filteredSchedules.length - 1}
                <div class="absolute left-5 top-12 w-0.5 h-full bg-surface-hover"></div>
              {/if}
              
              <!-- Time Badge -->
              <div class="flex-shrink-0 w-10 text-center">
                <div class="w-10 h-10 rounded-full {categoryInfo.color} flex items-center justify-center">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {@html categoryInfo.icon}
                  </svg>
                </div>
                <div class="text-xs text-muted mt-1">{schedule.startTime || "--:--"}</div>
              </div>

              <!-- Schedule Card -->
              <div class="flex-1 bg-surface rounded-2xl p-4 shadow-sm border border-default hover:shadow-md transition-shadow">
                <div class="flex items-start justify-between">
                  <div>
                    <h3 class="font-semibold text-primary">{schedule.title}</h3>
                    {#if schedule.location}
                      <p class="text-sm text-muted flex items-center gap-1 mt-1">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {schedule.location}
                      </p>
                    {/if}
                    {#if schedule.startTime && schedule.endTime}
                      <p class="text-sm text-muted mt-1">
                        {schedule.startTime} - {schedule.endTime}
                      </p>
                    {/if}
                    {#if schedule.memo}
                      <p class="text-sm text-secondary mt-2 bg-app p-2 rounded-lg">{schedule.memo}</p>
                    {/if}
                  </div>

                  <!-- Actions -->
                  <div class="flex gap-1">
                    <button
                      aria-label="일정 수정"
                      onclick={() => openEditModal(schedule)}
                      class="p-2 text-muted hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      aria-label="일정 삭제"
                      onclick={() => deleteSchedule(schedule.id)}
                      class="p-2 text-muted hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </main>

    <!-- Create/Edit Modal -->
    <Modal
      show={showCreateModal || editingSchedule !== null}
      title={editingSchedule ? "일정 수정" : "일정 추가"}
      onClose={closeModal}
    >
      <form onsubmit={(e) => { e.preventDefault(); editingSchedule ? updateSchedule() : createSchedule(); }} class="space-y-4">
        <div>
          <label for="title" class="label">제목</label>
          <input
            id="title"
            type="text"
            bind:value={newSchedule.title}
            class="input"
            placeholder="일정 제목"
            required
          />
        </div>

        <div>
          <label for="location" class="label">장소</label>
          <input
            id="location"
            type="text"
            bind:value={newSchedule.location}
            class="input"
            placeholder="장소 입력"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="startTime" class="label">시작 시간</label>
            <input
              id="startTime"
              type="time"
              bind:value={newSchedule.startTime}
              class="input"
            />
          </div>
          <div>
            <label for="endTime" class="label">종료 시간</label>
            <input
              id="endTime"
              type="time"
              bind:value={newSchedule.endTime}
              class="input"
            />
          </div>
        </div>

        <div>
          <span class="label">카테고리</span>
          <div class="grid grid-cols-3 gap-2" role="group" aria-label="카테고리 선택">
            {#each categories as cat}
              <button
                type="button"
                onclick={() => newSchedule.category = cat.key}
                class="p-3 rounded-lg text-center border-2 transition-colors
                  {newSchedule.category === cat.key
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-bright hover:border-bright'}"
              >
                <svg class="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {@html cat.icon}
                </svg>
                <p class="text-xs mt-1">{cat.label}</p>
              </button>
            {/each}
          </div>
        </div>

        <div>
          <label for="memo" class="label">메모</label>
          <textarea
            id="memo"
            bind:value={newSchedule.memo}
            class="input"
            rows="2"
            placeholder="메모 (선택)"
          ></textarea>
        </div>

        <div class="flex gap-3 pt-4">
          <Button type="button" variant="secondary" class="flex-1" onclick={closeModal}>취소</Button>
          <Button type="submit" variant="primary" class="flex-1" loading={loading}>{editingSchedule ? "수정하기" : "추가하기"}</Button>
        </div>
      </form>
    </Modal>
  </div>
</Layout>
